import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction, useMutation, useQuery } from "convex/react";
import { ArchiveIcon, ArchiveRestoreIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

export default function SkillEditor({
	skillId,
	onDeleted,
}: {
	skillId: Id<"clericalSkills">;
	onDeleted: () => void;
}) {
	const detail = useQuery(api.agent.skills.library.getSkillForStaff, {
		skillId,
	});
	const getSource = useAction(api.agent.skills.actions.getSkillSourceForStaff);
	const saveSkill = useAction(api.agent.skills.actions.saveSkill);
	const deleteSkill = useAction(api.agent.skills.actions.deleteSkill);
	const setArchived = useMutation(api.agent.skills.library.setSkillArchived);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [sourceDraft, setSource] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [archiving, setArchiving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [archiveOpen, setArchiveOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [previousDetail, setPreviousDetail] =
		useState<typeof detail>(undefined);

	if (detail !== previousDetail) {
		setPreviousDetail(detail);
		if (detail) {
			setName(detail.name);
			setDescription(detail.description);
		}
	}

	const sourceQuery = useReactQuery({
		queryKey: ["skill-source", skillId],
		queryFn: async () => {
			try {
				return await getSource({ skillId });
			} catch (error) {
				toast.error(
					error instanceof Error ? error.message : "Could not load source",
				);
				throw error;
			}
		},
		retry: false,
	});
	const source = sourceDraft ?? sourceQuery.data?.source ?? "";
	const sourceLoading = sourceQuery.isPending;

	if (detail === undefined) {
		return (
			<>
				<SheetHeader>
					<SheetTitle>Loading skill</SheetTitle>
					<SheetDescription>Loading current skill source.</SheetDescription>
				</SheetHeader>
				<div className="flex flex-col gap-4 px-4">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-80 w-full" />
				</div>
			</>
		);
	}
	if (detail === null) {
		return (
			<SheetHeader>
				<SheetTitle>Skill unavailable</SheetTitle>
				<SheetDescription>
					Skill was deleted or URL no longer points to a valid skill.
				</SheetDescription>
			</SheetHeader>
		);
	}

	const canSave =
		name.trim().length > 0 &&
		description.trim().length > 0 &&
		source.trim().length > 0;

	async function save(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!canSave) return;
		setSaving(true);
		try {
			await saveSkill({
				skillId,
				name,
				description,
				source,
			});
			toast.success("Skill saved");
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Could not save skill",
			);
		} finally {
			setSaving(false);
		}
	}

	async function updateArchive() {
		if (!detail) return;
		const archived = detail.status !== "archived";
		setArchiving(true);
		try {
			await setArchived({ skillId, archived });
			setArchiveOpen(false);
			toast.success(archived ? "Skill archived" : "Skill restored");
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Could not update skill",
			);
		} finally {
			setArchiving(false);
		}
	}

	async function removeSkill() {
		setDeleting(true);
		try {
			await deleteSkill({ skillId });
			setDeleteOpen(false);
			toast.success("Skill deleted");
			onDeleted();
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Could not delete skill",
			);
		} finally {
			setDeleting(false);
		}
	}

	return (
		<form className="flex min-h-0 flex-1 flex-col" onSubmit={save}>
			<SheetHeader>
				<div className="flex flex-wrap items-center gap-2 pr-8">
					<SheetTitle>{detail.name}</SheetTitle>
					<Badge
						variant={detail.status === "archived" ? "outline" : "secondary"}
					>
						{detail.status === "archived" ? "Archived" : "Active"}
					</Badge>
					<Badge variant="outline">
						{detail.invocationCount.toLocaleString()} loads
					</Badge>
				</div>
				<SheetDescription>skill://{detail.slug}</SheetDescription>
			</SheetHeader>

			<ScrollArea className="min-h-0 flex-1">
				<FieldGroup className="px-4 pb-4">
					<Field>
						<FieldLabel htmlFor="skill-name">Name</FieldLabel>
						<Input
							id="skill-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							maxLength={120}
						/>
						<FieldDescription>
							Reference remains skill://{detail.slug} when name changes.
						</FieldDescription>
					</Field>
					<Field>
						<FieldLabel htmlFor="skill-description">
							Routing description
						</FieldLabel>
						<Input
							id="skill-description"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
							maxLength={160}
						/>
						<FieldDescription>
							Generated on creation. Edit to refine when Clerical should load
							skill.
						</FieldDescription>
					</Field>
					<Field>
						<FieldLabel id="skill-source-label">Instructions</FieldLabel>
						{sourceLoading ? (
							<Skeleton className="h-96 w-full" />
						) : (
							<MarkdownEditor
								ariaLabelledBy="skill-source-label"
								value={source}
								onChange={setSource}
								placeholder="Write operational steps..."
							/>
						)}
					</Field>
					<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
						<span>{detail.size.toLocaleString()} bytes</span>
						<span className="font-mono">
							SHA-256 {detail.sha256.slice(0, 12)}…
						</span>
					</div>
				</FieldGroup>
			</ScrollArea>
			<SheetFooter className="border-t sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={() => setArchiveOpen(true)}
					>
						{detail.status === "archived" ? (
							<ArchiveRestoreIcon data-icon="inline-start" />
						) : (
							<ArchiveIcon data-icon="inline-start" />
						)}
						{detail.status === "archived" ? "Restore" : "Archive"}
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={() => setDeleteOpen(true)}
					>
						<Trash2Icon data-icon="inline-start" />
						Delete
					</Button>
				</div>
				<Button type="submit" disabled={!canSave || saving || sourceLoading}>
					{saving ? <Spinner data-icon="inline-start" /> : null}
					Save changes
				</Button>
			</SheetFooter>

			<AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{detail.status === "archived"
								? "Restore skill?"
								: "Archive skill?"}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{detail.status === "archived"
								? "Clerical can load this skill again."
								: "Clerical stops loading this skill until restored."}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => void updateArchive()}
							disabled={archiving}
						>
							{archiving ? <Spinner data-icon="inline-start" /> : null}
							{detail.status === "archived" ? "Restore" : "Archive"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete skill permanently?</AlertDialogTitle>
						<AlertDialogDescription>
							This cannot be undone. Workflows referencing skill://{detail.slug}{" "}
							fail until updated.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => void removeSkill()}
							disabled={deleting}
						>
							{deleting ? <Spinner data-icon="inline-start" /> : null}
							Delete skill
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</form>
	);
}
